import React from 'react';
import { useTheme } from '../context/ThemeContext';

const About = () => {
  const { isDarkMode } = useTheme();

  const sectionClass = `mb-12 p-6 rounded-2xl backdrop-blur-md shadow-lg border ${
    isDarkMode ? 'bg-gray-800/30 border-gray-700/30' : 'bg-white/30 border-gray-200/30'
  }`;

  const headingClass = `text-3xl font-medium mb-6 ${isDarkMode ? 'text-white' : 'text-gray-800'}`;
  const subHeadingClass = `text-xl font-medium mb-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`;
  const textClass = `text-base leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`;
  const highlightClass = `font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`;
  const dateClass = `text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`;

  return (
    <div className="max-w-5xl mx-auto px-4 py-24">
      {/* Professional Summary */}
      <section className={sectionClass}>
        <h2 className={headingClass}>Professional Summary</h2>
        <p className={textClass}>
          PhD-qualified engineer with 9+ years medical device experience. Expertise in FMEA, risk management (ISO 14971), 
          design controls, V&V, and regulatory compliance (ISO 13485) for Class II/III devices. Skilled in Python, C#, CAD 
          (SolidWorks), and cross-functional technical leadership.
        </p>
      </section>

      {/* Core Competencies */}
      <section className={sectionClass}>
        <h2 className={headingClass}>Core Competencies</h2>
        <div className="flex flex-wrap gap-2">
          {['ISO 13485 (QMS)', 'ISO 14971 (Risk Management)', 'FMEA', 'Design Controls', 'V&V', 'DHF', 'CAPA', 
            'Root Cause Analysis', 'Requirements Engineering', 'GLP', 'SOP Development', 'Python/C#/MATLAB', 
            'SolidWorks/Fusion360', 'Agile/Jira'].map((skill) => (
            <span
              key={skill}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg backdrop-blur-sm ${
                isDarkMode
                  ? 'bg-gray-700/40 text-gray-200 border border-gray-600/40'
                  : 'bg-gray-100/40 text-gray-800 border border-gray-200/40'
              }`}
            >
              {skill}
            </span>
          ))}
        </div>
      </section>

      {/* Work Experience Section */}
      <section className={sectionClass}>
        <h2 className={headingClass}>Professional Experience</h2>
        
        {/* ANGLE Plc */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-2">
            <h3 className={subHeadingClass}>Technical Support Scientist, Senior Systems Engineer | ANGLE Plc</h3>
            <span className={dateClass}>Feb 2025 – Present</span>
          </div>
          <p className={`${textClass} mb-2`}>Guildford, UK</p>
          <ul className="list-disc pl-6 space-y-2">
            <li className={textClass}>Lead FMEA processes for engineering teams identifying 40+ failure modes and implementing ISO 14971-compliant risk mitigation</li>
            <li className={textClass}>Project management for warehouse inventory system and design changes for core technologies through design control processes</li>
            <li className={textClass}>Designed validation procedures for 40,000+ cassette defect identification using statistical sampling methods</li>
            <li className={textClass}>Develop SOPs standardising quality processes; conduct qualification/calibration ensuring ISO 13485 compliance</li>
            <li className={textClass}>Manage junior development engineer; collaborate with cross-functional teams implementing CAPA based on FMEA findings</li>
            <li className={textClass}>Spearhead software development improving device functionality; conduct systematic literature reviews for CTCs</li>
            <li className={textClass}>Maintain DHF records for regulatory audit readiness; drive continuous process improvement initiatives</li>
          </ul>
        </div>

        {/* Proxisense */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-2">
            <h3 className={subHeadingClass}>R&D Engineer | Proxisense</h3>
            <span className={dateClass}>Sep 2023 – Nov 2024</span>
          </div>
          <p className={`${textClass} mb-2`}>Farnborough, UK</p>
          <ul className="list-disc pl-6 space-y-2">
            <li className={textClass}>Developed skin cancer detection device from concept through prototype for NHS deployment meeting regulatory requirements</li>
            <li className={textClass}>Designed and deployed desktop application (Python, C#, GUI) integrating thermal sensing technology for data acquisition</li>
            <li className={textClass}>Created automated testing system reducing measurement time from 2 minutes to 5 seconds (98% improvement)</li>
            <li className={textClass}>Conducted laboratory experiments with data analysis improving product efficiency by 25%</li>
            <li className={textClass}>Applied Agile methodology (Jira/Confluence) ensuring timely deliverables; maintained traceability between requirements and V&V</li>
          </ul>
        </div>

        {/* University of Leeds - Researcher */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-2">
            <h3 className={subHeadingClass}>PhD Researcher - Industrial Partnership (DePuy Synthes/J&J) | University of Leeds</h3>
            <span className={dateClass}>Oct 2018 – Apr 2024</span>
          </div>
          <p className={`${textClass} mb-2`}>Leeds, UK</p>
          <ul className="list-disc pl-6 space-y-2">
            <li className={textClass}>Pioneered novel pre-clinical testing methodology for hip simulation improving surgical planning and device validation</li>
            <li className={textClass}>Developed tissue engineering approaches for orthopaedics resulting in peer-reviewed publications (PLOS One)</li>
            <li className={textClass}>Collaborated with DePuy Synthes R&D engineers and clinical advisors throughout 6-year partnership</li>
            <li className={textClass}>Designed custom fixtures and protocols for biomechanical fatigue testing; applied GLP principles for regulatory acceptance</li>
            <li className={textClass}>Supervised master's students leading to successful publication; conducted advanced imaging (multiphoton, Raman spectroscopy)</li>
          </ul>
        </div>
      </section>

      {/* Education Section */}
      <section className={sectionClass}>
        <h2 className={headingClass}>Education</h2>
        
        {/* PhD */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-2">
            <h3 className={subHeadingClass}>PhD w. integrated MSc, Tissue Engineering & Regenerative Medicine</h3>
            <span className={dateClass}>2018 – 2023</span>
          </div>
          <p className={`${textClass} mb-2`}>University of Leeds, UK</p>
          <p className={`${textClass} mb-2`}><span className={highlightClass}>Specialization:</span> Innovation in Medical and Biological Engineering</p>
          <ul className="list-disc pl-6 space-y-2">
            <li className={textClass}>Industrial CASE Partnership with DePuy Synthes (Johnson & Johnson)</li>
            <li className={textClass}>Pioneered novel pre-clinical testing methodology for hip simulation</li>
            <li className={textClass}>Published peer-reviewed research in PLOS One</li>
          </ul>
        </div>

        {/* MEng */}
        <div>
          <div className="flex justify-between items-start mb-2">
            <h3 className={subHeadingClass}>MEng, Biomedical Engineering (First Class Honours)</h3>
            <span className={dateClass}>2013 – 2017</span>
          </div>
          <p className={`${textClass} mb-2`}>Queen Mary University of London, UK</p>
          <ul className="list-disc pl-6 space-y-2">
            <li className={textClass}><span className={highlightClass}>Key project:</span> Intramedullary nail design through FEA</li>
            <li className={textClass}><span className={highlightClass}>Modules:</span> Implant Design, Medical Device Development</li>
          </ul>
        </div>
      </section>

      {/* Skills & Certifications Section */}
      <section className={sectionClass}>
        <h2 className={headingClass}>Certifications & Skills</h2>
        
        {/* Certifications */}
        <div className="mb-8">
          <h3 className={subHeadingClass}>Certifications</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li className={textClass}>Lean Six Sigma Green Belt</li>
            <li className={textClass}>Lean Six Sigma Yellow Belt</li>
            <li className={textClass}>Certified SolidWorks Associate</li>
          </ul>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Technical Skills */}
          <div>
            <h3 className={subHeadingClass}>Technical Skills</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li className={textClass}><span className={highlightClass}>Programming:</span> Python, C#, MATLAB</li>
              <li className={textClass}><span className={highlightClass}>CAD:</span> SolidWorks, Fusion360, Inventor, AutoCAD</li>
              <li className={textClass}><span className={highlightClass}>Analysis:</span> Statistical Analysis, Biomechanical Testing</li>
            </ul>
          </div>

          {/* Languages */}
          <div>
            <h3 className={subHeadingClass}>Languages</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li className={textClass}>English (Native)</li>
              <li className={textClass}>Hindi (Professional)</li>
              <li className={textClass}>German (Elementary)</li>
              <li className={textClass}>French (Limited)</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Publications Section */}
      <section className={sectionClass}>
        <h2 className={headingClass}>Publications</h2>
        <ul className="space-y-4">
          <li className={textClass}>
            Jimenez-Cruz D, Dubey M, et al. (2022) "An in vitro methodology for experimental simulation on the natural hip joint." 
            <span className={highlightClass}> PLoS One. 17(8):e0272264</span>
          </li>
          <li className={textClass}>
            Dubey M, et al. (2023) "Investigating effects of mechanical damage on the microstructure at the chondrolabral junction in the hip." 
            <span className={highlightClass}> Orthopaedic Research Society</span>
          </li>
        </ul>
      </section>
    </div>
  );
};

export default About;
